import {
  ChangeEvent,
  ChangeEventHandler,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import { Box, Flex, StyleProps, TabPanel, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';

import { Deal, DealStateEnum } from '../../gql/dealGql';
import {
  Bank,
  DealType,
  useCreditDecisionApplicationNumberQuery,
  useDealersQuery,
  useGetCreditDecisionByProviderLenderQuery,
} from '../../gql/generated/graphql';

import PrequalificationSection from '../CreditApplication/components/Prequalification/PrequalificationSection';
import CreditDecisionsButton from '../CreditDecisions/CreditDecisionsButton';
import EditDealInfoButton from '../EditDealInfoButton';
import { TotalPayoff } from '../TotalPayoff/TotalPayoff';
import NumberInput from '../shared/NumberInput';
import Switch from '../shared/Switch';
import { BankAndPaymentsSection } from './components/BankAndPaymentsSection';
import InputRow from './components/InputRow';
import PaymentOptions from './components/PaymentOptions/PaymentOptions';
import { ProductsSection } from './components/ProductsSection';
import { TitleAndRegistrationSection } from './components/TitleAndRegistrationSection';
import TopSection from './components/TopSection';
import TopSectionData from './components/TopSectionData';
import SaveEditButton from './components/buttons/SaveEditButton';

import {
  DebouncedSave,
  dividerProps,
  sectionWidth,
  sectionsContainerProps,
} from './DealInfoBuyoutForm';
import { CARD_PAYMENT_AMOUNT_MAX, CARD_PAYMENT_AMOUNT_MIN } from './constants';

import { isBeforeClosing, isDealInOrPastFunded } from '../../constants/dealStates';
import { LDFlags } from '../../constants/experiments';
import { PermissionEnum } from '../../constants/permissions';
import useFlag from '../../hooks/useFlag';
import { DealContext } from '../../libs/DealContext';
import { AbilityContext } from '../../libs/contextLib';
import { formatMoney } from '../../libs/utils';
import { isInputReadOnly } from '../../utils/permissions';

const sectionMarginTop: StyleProps['mt'] = 12;
const smallSectionMarginTop: StyleProps['mt'] = 5;
const smallerSectionMarginTop: StyleProps['mt'] = 3;

type DealStructurePanelProps = {
  debouncedSave: DebouncedSave;
  banks: Bank[];
  isEditable: boolean;
  isEditingWithPermission: boolean;
  setIsEditingWithPermission: Dispatch<SetStateAction<boolean>>;
  customHandleChange?: ChangeEventHandler<HTMLInputElement | HTMLSelectElement>;
};

export const DealStructurePanel = ({
  debouncedSave,
  banks,
  isEditable,
  isEditingWithPermission,
  setIsEditingWithPermission,
  customHandleChange,
}: DealStructurePanelProps) => {
  const abilities = useContext(AbilityContext);
  const { deal, isRecalculatingPayoff, setIsRecalculatingPayoff, setUserChangedSomething } =
    useContext(DealContext);
  const { values, setFieldValue } = useFormikContext<Deal>();

  // Bridges PaymentOptions' "re-persist current package" fn to ProductsSection's
  // VSC/Calc Save buttons (siblings), so a selected rate is written to
  // deal_products instead of only living in Formik.
  const persistSelectedPackageRef = useRef<(() => Promise<void>) | null>(null);

  const { data: creditDecisionApplicationNumberData } = useCreditDecisionApplicationNumberQuery({
    skip: !deal.financial_info.selected_credit_decision_id,
    variables: {
      id: Number(deal.financial_info.selected_credit_decision_id) ?? -1,
    },
  });

  const { data: getCreditDecisionByProviderLenderData } = useGetCreditDecisionByProviderLenderQuery(
    {
      skip:
        !!deal.financial_info.selected_credit_decision_id ||
        !deal?.financial_info?.new_lienholder?.provider_lender_id ||
        !deal?.id,
      variables: {
        deal_id: deal.id ?? -1,
        provider_lender_id: deal.financial_info.new_lienholder?.provider_lender_id || '',
      },
    },
  );

  const isMarketplaceFinalizedDeal =
    deal.type === DealType.Marketplace && deal.state === DealStateEnum.Finalized;

  const { data: dealersData } = useDealersQuery({
    fetchPolicy: 'cache-first',
    skip: !isMarketplaceFinalizedDeal,
  });

  const applicationNumber =
    creditDecisionApplicationNumberData?.creditDecisionV2?.provider_decision_id ||
    getCreditDecisionByProviderLenderData?.getCreditDecisionByProviderLender?.provider_decision_id;

  const selectedDealer = useMemo(() => {
    if (!deal.selected_dealer_id) {
      return null;
    }

    return dealersData?.dealers?.find((dealer) => dealer?.id === deal.selected_dealer_id) ?? null;
  }, [deal.selected_dealer_id, dealersData?.dealers]);

  const inputIsReadOnlyByDealState = isInputReadOnly({
    abilities,
    dealState: values.state,
    determineReadOnlyByDealState: true,
    isEditing: isEditingWithPermission,
  });
  const inputIsReadOnlyByPermissions = isInputReadOnly({
    abilities,
    dealState: values.state,
    determineReadOnlyByDealState: false,
    isEditing: isEditingWithPermission,
  });

  const breakOutCreditAndDebitCards = useFlag(LDFlags.BREAK_OUT_CREDIT_AND_DEBIT_CARDS);
  const showCardPaymentLimitToggle =
    abilities.has(PermissionEnum.ChangeCardPaymentAmountLimit) &&
    (deal.financial_info?.money_down ?? 0) > CARD_PAYMENT_AMOUNT_MIN &&
    !isBeforeClosing(deal.state);

  const dealIsInOrPastFunded = useMemo(() => isDealInOrPastFunded(deal.state), [deal.state]);

  const handleCardLimitChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUserChangedSomething(true);
    if (e.target.checked) {
      setFieldValue('financial_info.credit_card_payment_amount_limit', CARD_PAYMENT_AMOUNT_MAX);
    } else {
      setFieldValue('financial_info.credit_card_payment_amount_limit', CARD_PAYMENT_AMOUNT_MIN);
    }
  };

  useEffect(() => {
    if (
      deal.financial_info?.money_down &&
      deal.financial_info.money_down <= CARD_PAYMENT_AMOUNT_MIN
    ) {
      setFieldValue('financial_info.credit_card_payment_amount_limit', CARD_PAYMENT_AMOUNT_MIN);
    }
  }, [deal.financial_info?.money_down]);

  useEffect(() => {
    // Set in `Funded`, when we know the reserve amount. It was being set too early, in `Structuring`, before the other fields were adjusted.
    if (
      dealIsInOrPastFunded &&
      !deal.financial_info?.actual_reserve &&
      deal.financial_info?.reserve
    ) {
      setUserChangedSomething(true);
      setFieldValue('financial_info.actual_reserve', deal.financial_info.reserve);
    }
  }, [dealIsInOrPastFunded, deal.financial_info?.reserve]);

  return (
    <TabPanel p={0}>
      <TopSection justifyContent="space-between">
        <TopSectionData label="Bank Application #">{applicationNumber}</TopSectionData>
        <Box alignSelf="center">
          <CreditDecisionsButton isEditingWithPermission={isEditingWithPermission} />
          <PrequalificationSection inStructuringOrBeyond />
        </Box>
      </TopSection>

      <PaymentOptions
        debounceSave={debouncedSave}
        isEditingWithPermission={isEditingWithPermission}
        customHandleChange={customHandleChange}
        persistSelectedPackageRef={persistSelectedPackageRef}
      />

      <Flex {...sectionsContainerProps}>
        <Flex direction="column" w={sectionWidth}>
          <InputRow label="Vehicle Payoff">
            <Text variant="dealInfoBuyout">{formatMoney(values.car?.payoff?.vehicle_payoff)}</Text>
          </InputRow>
          <InputRow label="Money Down">
            <NumberInput
              name="financial_info.money_down"
              isMoney
              showThousandSeparator
              additionalHandleChange={customHandleChange}
              isDisabled={inputIsReadOnlyByDealState || !isEditable}
            />
          </InputRow>
          {showCardPaymentLimitToggle && (
            <InputRow label={`${breakOutCreditAndDebitCards ? 'Credit Card' : 'Card'} Limit`}>
              <Switch
                id="credit_card_payment_amount_limit"
                name="financial_info.credit_card_payment_amount_limit"
                isNoYes
                noYesFontSize="sm"
                noLabel="$1k"
                yesLabel="$3k"
                customHandleChange={handleCardLimitChange}
                switchProps={{
                  isChecked:
                    values.financial_info.credit_card_payment_amount_limit ===
                    CARD_PAYMENT_AMOUNT_MAX,
                }}
                py={2}
              />
            </InputRow>
          )}
          <InputRow label="Bank Fees">
            <NumberInput
              name="financial_info.bank_fees"
              isMoney
              showThousandSeparator
              additionalHandleChange={customHandleChange}
            />
          </InputRow>
          {dealIsInOrPastFunded ? (
            <InputRow label="Actual Reserve">
              <NumberInput
                name="financial_info.actual_reserve"
                isMoney
                showThousandSeparator
                isDisabled={!abilities.has(PermissionEnum.EditActualReserve)}
                additionalHandleChange={customHandleChange}
              />
            </InputRow>
          ) : null}
          <TitleAndRegistrationSection
            mt={sectionMarginTop}
            isEditable={isEditable}
            debouncedSave={debouncedSave}
          />
        </Flex>

        <Flex direction="column" {...dividerProps} />

        <Flex direction="column" w={sectionWidth}>
          <BankAndPaymentsSection
            debouncedSave={debouncedSave}
            dealDetailsPageBeingEdited={isEditable}
            inputIsReadOnlyByPermissions={inputIsReadOnlyByPermissions}
            banks={banks}
          />

          <EditDealInfoButton
            isEditingWithPermission={isEditingWithPermission}
            setIsEditingWithPermission={setIsEditingWithPermission}
            mt={smallSectionMarginTop}
          />
          <SaveEditButton
            isEditingWithPermission={isEditingWithPermission}
            setIsEditingWithPermission={setIsEditingWithPermission}
            mt={smallSectionMarginTop}
          />

          <ProductsSection
            mt={smallerSectionMarginTop}
            isEditable={isEditable}
            inputIsReadOnlyByPermissions={inputIsReadOnlyByPermissions}
            customHandleChange={customHandleChange}
            persistSelectedPackageRef={persistSelectedPackageRef}
          />
        </Flex>
      </Flex>

      <Flex {...sectionsContainerProps} align="center" justify="right" mr={10}>
        {isMarketplaceFinalizedDeal && selectedDealer ? (
          <Text mr={3}>Dealer: {selectedDealer.name}</Text>
        ) : null}
        <Text>Amount Financed</Text>
        <Text p={2} bgColor="azureishWhite" fontSize="26px" fontWeight="normal">
          {formatMoney(values.financial_info?.amount_financed)}
        </Text>
      </Flex>

      {isEditable ? (
        <TotalPayoff
          hidden
          isRecalculating={isRecalculatingPayoff}
          setIsRecalculating={setIsRecalculatingPayoff}
        />
      ) : null}
    </TabPanel>
  );
};
